import React, { useEffect } from "react";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "./AIAgents.css";

import {
    FiArrowRight,
    FiCpu,
    FiZap,
    FiCheckCircle,
    FiTarget,
    FiGitBranch,
    FiDatabase,
    FiMessageSquare,
    FiLayers,
    FiActivity,
    FiSettings,
    FiUsers,
    FiSearch,
    FiRepeat,
    FiShoppingBag,
    FiHeart,
    FiTruck,
    FiDollarSign,
    FiBriefcase,
    FiBox,
} from "react-icons/fi";

const AIAgents = () => {
    useEffect(() => {
        document.title = "Dropgear IO | AI Agents";
    }, []);

    return (
        <>
            <Header />

            <main className="ai-agents-page">

                {/* =====================================================
            HERO
        ===================================================== */}

                <section className="agents-hero">

                    <div className="agents-hero-grid">

                        {/* LEFT */}

                        <div className="agents-hero-content">

                            <span className="agents-eyebrow">
                                AI AGENTS
                            </span>

                            <h1>
                                AI that can
                                <span> move work forward.</span>
                            </h1>

                            <p>
                                Dropgear IO helps businesses build intelligent
                                AI agents that can understand goals, work with
                                information, use connected tools, and complete
                                defined tasks across business workflows.
                            </p>

                            <div className="agents-hero-actions">

                                <a
                                    href="/book-demo"
                                    className="agents-primary-btn"
                                >
                                    Build an AI Agent
                                    <FiArrowRight />
                                </a>

                                <a
                                    href="#agent-capabilities"
                                    className="agents-secondary-btn"
                                >
                                    Explore AI Agents
                                </a>

                            </div>

                            <div className="agents-hero-trust">

                                <FiCheckCircle />

                                <span>
                                    Designed around practical business tasks
                                </span>

                            </div>

                        </div>


                        {/* RIGHT */}

                        <div className="agents-visual">

                            <div className="agents-glow"></div>

                            {/* CONNECTION LINES */}

                            <div className="agents-orbit orbit-one"></div>
                            <div className="agents-orbit orbit-two"></div>

                            {/* MAIN AGENT PANEL */}

                            <div className="agents-dashboard">

                                <div className="agents-dashboard-header">

                                    <div>

                                        <span className="agents-dashboard-label">
                                            AI AGENT
                                        </span>

                                        <strong>
                                            Operations Agent
                                        </strong>

                                    </div>

                                    <span className="agents-active">
                                        <i></i>
                                        Active
                                    </span>

                                </div>


                                {/* AGENT BRAIN */}

                                <div className="agent-brain">

                                    <div className="agent-brain-ring ring-one"></div>
                                    <div className="agent-brain-ring ring-two"></div>
                                    <div className="agent-brain-ring ring-three"></div>

                                    <div className="agent-brain-core">
                                        <FiCpu />
                                    </div>

                                    <div className="agent-pulse pulse-one"></div>
                                    <div className="agent-pulse pulse-two"></div>
                                    <div className="agent-pulse pulse-three"></div>

                                </div>


                                {/* AGENT TASK */}

                                <div className="agent-current-task">

                                    <div className="agent-task-icon">
                                        <FiTarget />
                                    </div>

                                    <div>

                                        <span>
                                            CURRENT OBJECTIVE
                                        </span>

                                        <strong>
                                            Review incoming requests
                                        </strong>

                                    </div>

                                    <div className="agent-task-status">
                                        Working
                                    </div>

                                </div>


                                {/* AGENT STEPS */}

                                <div className="agent-task-flow">

                                    <div className="agent-flow-step completed">
                                        <FiCheckCircle />
                                        <span>Understand</span>
                                    </div>

                                    <div className="agent-flow-step completed">
                                        <FiCheckCircle />
                                        <span>Analyze</span>
                                    </div>

                                    <div className="agent-flow-step active">
                                        <FiActivity />
                                        <span>Decide</span>
                                    </div>

                                    <div className="agent-flow-step">
                                        <FiZap />
                                        <span>Act</span>
                                    </div>

                                </div>

                            </div>


                            {/* FLOATING CARDS */}

                            <div className="agent-floating-card agent-float-one">

                                <FiSearch />

                                <span>
                                    Information found
                                </span>

                            </div>


                            <div className="agent-floating-card agent-float-two">

                                <FiCheckCircle />

                                <span>
                                    Task completed
                                </span>

                            </div>


                            <div className="agent-floating-card agent-float-three">

                                <FiZap />

                                <span>
                                    Action executed
                                </span>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            INTRO
        ===================================================== */}

                <section className="agents-intro">

                    <div className="agents-container">

                        <div className="agents-intro-heading">

                            <span>
                                BEYOND CHATBOTS
                            </span>

                            <h2>
                                Give AI a goal.
                                <br />
                                <strong>Let it work through the task.</strong>
                            </h2>

                            <p>
                                AI agents can go beyond responding to individual
                                prompts. They can be designed to work through
                                defined objectives, interact with information,
                                use tools, and progress through multiple steps
                                while operating within the boundaries you define.
                            </p>

                        </div>


                        <div className="agents-intro-grid">

                            <div className="agents-intro-card">

                                <div className="agents-intro-number">
                                    01
                                </div>

                                <FiTarget />

                                <h3>
                                    Understand the Goal
                                </h3>

                                <p>
                                    Define what the agent is expected to accomplish
                                    and the conditions around the task.
                                </p>

                            </div>


                            <div className="agents-intro-card">

                                <div className="agents-intro-number">
                                    02
                                </div>

                                <FiSearch />

                                <h3>
                                    Gather Context
                                </h3>

                                <p>
                                    Agents can work with relevant information and
                                    connected resources required for the task.
                                </p>

                            </div>


                            <div className="agents-intro-card">

                                <div className="agents-intro-number">
                                    03
                                </div>

                                <FiZap />

                                <h3>
                                    Take Action
                                </h3>

                                <p>
                                    The agent can perform defined actions and
                                    continue the workflow toward its objective.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            CAPABILITIES
        ===================================================== */}

                <section
                    id="agent-capabilities"
                    className="agents-capabilities"
                >

                    <div className="agents-container">

                        <div className="agents-section-heading">

                            <span>
                                AI AGENT CAPABILITIES
                            </span>

                            <h2>
                                Agents designed to
                                <br />
                                <strong>do more than respond.</strong>
                            </h2>

                            <p>
                                Build purpose-driven AI agents around the tasks,
                                information, systems, and workflows your business
                                actually uses.
                            </p>

                        </div>


                        <div className="agents-capabilities-grid">

                            {/* 01 */}

                            <div className="agents-capability-card featured">

                                <div className="agents-capability-icon">
                                    <FiTarget />
                                </div>

                                <span className="agents-card-number">
                                    01
                                </span>

                                <h3>
                                    Goal-Oriented Agents
                                </h3>

                                <p>
                                    Define an objective and design an agent around
                                    the steps required to work toward that outcome.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Defined objectives
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Multi-step tasks
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Controlled actions
                                    </li>

                                </ul>

                            </div>


                            {/* 02 */}

                            <div className="agents-capability-card">

                                <div className="agents-capability-icon">
                                    <FiCpu />
                                </div>

                                <span className="agents-card-number">
                                    02
                                </span>

                                <h3>
                                    Intelligent Reasoning
                                </h3>

                                <p>
                                    Agents can evaluate available information and
                                    determine the next step within a defined process.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Context processing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Task evaluation
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Step selection
                                    </li>

                                </ul>

                            </div>


                            {/* 03 */}

                            <div className="agents-capability-card">

                                <div className="agents-capability-icon">
                                    <FiLayers />
                                </div>

                                <span className="agents-card-number">
                                    03
                                </span>

                                <h3>
                                    Tool-Enabled Agents
                                </h3>

                                <p>
                                    Connect agents with defined tools and systems
                                    so they can perform actions as part of a workflow.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Tool connections
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        System actions
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow execution
                                    </li>

                                </ul>

                            </div>


                            {/* 04 */}

                            <div className="agents-capability-card">

                                <div className="agents-capability-icon">
                                    <FiDatabase />
                                </div>

                                <span className="agents-card-number">
                                    04
                                </span>

                                <h3>
                                    Context-Aware Agents
                                </h3>

                                <p>
                                    Give agents access to the relevant information
                                    needed to understand the task they are handling.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Business information
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Context retrieval
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Structured knowledge
                                    </li>

                                </ul>

                            </div>


                            {/* 05 */}

                            <div className="agents-capability-card">

                                <div className="agents-capability-icon">
                                    <FiGitBranch />
                                </div>

                                <span className="agents-card-number">
                                    05
                                </span>

                                <h3>
                                    Multi-Step Execution
                                </h3>

                                <p>
                                    Break complex work into smaller steps and
                                    coordinate them through a defined agent workflow.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Task sequencing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow coordination
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Progress tracking
                                    </li>

                                </ul>

                            </div>


                            {/* 06 */}

                            <div className="agents-capability-card">

                                <div className="agents-capability-icon">
                                    <FiUsers />
                                </div>

                                <span className="agents-card-number">
                                    06
                                </span>

                                <h3>
                                    Human-in-the-Loop
                                </h3>

                                <p>
                                    Keep people involved at the points where human
                                    review, approval, or intervention is important.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Review points
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Approval workflows
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Human oversight
                                    </li>

                                </ul>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            HOW AGENTS WORK
        ===================================================== */}

                <section className="agents-how-section">

                    <div className="agents-container">

                        <div className="agents-section-heading">

                            <span>
                                HOW AI AGENTS WORK
                            </span>

                            <h2>
                                From objective
                                <br />
                                <strong>to action.</strong>
                            </h2>

                            <p>
                                A practical AI agent follows a structured cycle:
                                understand the objective, gather context, determine
                                the next step, act, and evaluate the result.
                            </p>

                        </div>


                        <div className="agents-process">

                            <div className="agents-process-step">

                                <span>01</span>

                                <div className="agents-process-icon">
                                    <FiTarget />
                                </div>

                                <h3>
                                    Goal
                                </h3>

                                <p>
                                    Define what the agent needs to accomplish.
                                </p>

                            </div>


                            <div className="agents-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="agents-process-step">

                                <span>02</span>

                                <div className="agents-process-icon">
                                    <FiSearch />
                                </div>

                                <h3>
                                    Context
                                </h3>

                                <p>
                                    Gather information relevant to the task.
                                </p>

                            </div>


                            <div className="agents-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="agents-process-step">

                                <span>03</span>

                                <div className="agents-process-icon">
                                    <FiCpu />
                                </div>

                                <h3>
                                    Reason
                                </h3>

                                <p>
                                    Evaluate information and determine the next step.
                                </p>

                            </div>


                            <div className="agents-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="agents-process-step">

                                <span>04</span>

                                <div className="agents-process-icon">
                                    <FiZap />
                                </div>

                                <h3>
                                    Act
                                </h3>

                                <p>
                                    Perform the defined action through connected tools.
                                </p>

                            </div>


                            <div className="agents-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="agents-process-step">

                                <span>05</span>

                                <div className="agents-process-icon">
                                    <FiActivity />
                                </div>

                                <h3>
                                    Evaluate
                                </h3>

                                <p>
                                    Check the result and continue or request review.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            AGENT TYPES
        ===================================================== */}

                <section className="agent-types-section">

                    <div className="agents-container">

                        <div className="agents-section-heading centered">

                            <span>
                                PURPOSE-BUILT AI AGENTS
                            </span>

                            <h2>
                                One platform.
                                <br />
                                <strong>Different kinds of agents.</strong>
                            </h2>

                            <p>
                                AI agents can be designed around specific business
                                responsibilities rather than trying to solve every
                                problem with one general-purpose system.
                            </p>

                        </div>


                        <div className="agent-types-grid">

                            <div className="agent-type-card">

                                <FiMessageSquare />

                                <h3>
                                    Customer Agent
                                </h3>

                                <p>
                                    Helps handle defined customer interactions,
                                    information requests, and support workflows.
                                </p>

                                <span>01</span>

                            </div>


                            <div className="agent-type-card">

                                <FiBriefcase />

                                <h3>
                                    Operations Agent
                                </h3>

                                <p>
                                    Works through internal operational tasks,
                                    requests, and structured business processes.
                                </p>

                                <span>02</span>

                            </div>


                            <div className="agent-type-card">

                                <FiSearch />

                                <h3>
                                    Research Agent
                                </h3>

                                <p>
                                    Helps gather, organize, and summarize information
                                    for defined research workflows.
                                </p>

                                <span>03</span>

                            </div>


                            <div className="agent-type-card">

                                <FiDatabase />

                                <h3>
                                    Data Agent
                                </h3>

                                <p>
                                    Helps work with structured business information
                                    and defined data-processing tasks.
                                </p>

                                <span>04</span>

                            </div>


                            <div className="agent-type-card">

                                <FiUsers />

                                <h3>
                                    Team Assistant
                                </h3>

                                <p>
                                    Supports teams with recurring information,
                                    coordination, and productivity workflows.
                                </p>

                                <span>05</span>

                            </div>


                            <div className="agent-type-card">

                                <FiSettings />

                                <h3>
                                    Workflow Agent
                                </h3>

                                <p>
                                    Coordinates multiple steps in a defined workflow
                                    and helps move tasks from one stage to another.
                                </p>

                                <span>06</span>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            INDUSTRIES
        ===================================================== */}

                <section className="agents-industries">

                    <div className="agents-container">

                        <div className="agents-section-heading centered">

                            <span>
                                AI AGENTS ACROSS INDUSTRIES
                            </span>

                            <h2>
                                Agents built around
                                <br />
                                <strong>real business work.</strong>
                            </h2>

                            <p>
                                Purpose-built agents can support different
                                operational workflows across industries.
                            </p>

                        </div>


                        <div className="agents-industry-grid">

                            <div className="agents-industry-card">

                                <FiShoppingBag />

                                <h3>
                                    Retail & Ecommerce
                                </h3>

                                <p>
                                    Customer assistance, product workflows,
                                    order-related tasks, and operational support.
                                </p>

                            </div>


                            <div className="agents-industry-card">

                                <FiHeart />

                                <h3>
                                    Healthcare
                                </h3>

                                <p>
                                    Administrative assistance, information workflows,
                                    scheduling support, and internal operations.
                                </p>

                            </div>


                            <div className="agents-industry-card">

                                <FiTruck />

                                <h3>
                                    Logistics
                                </h3>

                                <p>
                                    Operational coordination, status information,
                                    routing tasks, and workflow assistance.
                                </p>

                            </div>


                            <div className="agents-industry-card">

                                <FiDollarSign />

                                <h3>
                                    Finance
                                </h3>

                                <p>
                                    Information processing, internal requests,
                                    document workflows, and operational assistance.
                                </p>

                            </div>


                            <div className="agents-industry-card">

                                <FiBriefcase />

                                <h3>
                                    Professional Services
                                </h3>

                                <p>
                                    Research, client workflows, internal assistance,
                                    and knowledge-based operational tasks.
                                </p>

                            </div>


                            <div className="agents-industry-card">

                                <FiBox />

                                <h3>
                                    Enterprise Operations
                                </h3>

                                <p>
                                    Cross-team coordination, workflow support,
                                    information handling, and process assistance.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            WHY AGENTS
        ===================================================== */}

                <section className="agents-benefits">

                    <div className="agents-container">

                        <div className="agents-benefits-grid">

                            <div className="agents-benefits-content">

                                <span>
                                    WHY AI AGENTS
                                </span>

                                <h2>
                                    Move from
                                    <br />
                                    <strong>asking AI to doing with AI.</strong>
                                </h2>

                                <p>
                                    AI agents create a bridge between intelligent
                                    models and practical business tasks. Instead of
                                    simply producing an answer, an agent can be
                                    designed around a defined objective and workflow.
                                </p>

                                <a
                                    href="/contact"
                                    className="agents-primary-btn"
                                >
                                    Discuss Your Use Case
                                    <FiArrowRight />
                                </a>

                            </div>


                            <div className="agents-benefit-list">

                                <div>

                                    <FiTarget />

                                    <section>

                                        <h3>
                                            Goal-focused
                                        </h3>

                                        <p>
                                            Design agents around specific outcomes and
                                            responsibilities.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiLayers />

                                    <section>

                                        <h3>
                                            Connected to tools
                                        </h3>

                                        <p>
                                            Give agents access to the systems and tools
                                            required for defined tasks.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiGitBranch />

                                    <section>

                                        <h3>
                                            Multi-step capable
                                        </h3>

                                        <p>
                                            Structure agents to work through sequences
                                            rather than isolated interactions.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiUsers />

                                    <section>

                                        <h3>
                                            Human oversight
                                        </h3>

                                        <p>
                                            Keep people involved wherever review,
                                            approval, or intervention is required.
                                        </p>

                                    </section>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            CTA
        ===================================================== */}

                <section className="agents-final-cta">

                    <div className="agents-final-glow"></div>

                    <div className="agents-final-content">

                        <span>
                            READY TO BUILD WITH AI?
                        </span>

                        <h2>
                            Turn a business task
                            <br />
                            into an AI agent.
                        </h2>

                        <p>
                            Tell Dropgear IO about a workflow, operational
                            responsibility, or repetitive task that could
                            benefit from an intelligent agent.
                        </p>

                        <a
                            href="/contact"
                            className="agents-cta-button"
                        >
                            Start a Conversation
                            <FiArrowRight />
                        </a>

                    </div>

                </section>

            </main>

            <Footer />
        </>
    );
};

export default AIAgents;